<div class="workspace-page">
  <div class="workspace-page__panel workspace-page__panel--card">
    <header class="workspace-page__heading">
      <h1 class="workspace-page__title">Snoozed</h1>
    </header>

  <!-- The same control the Board carries. These pages stopped being cards on
       the Dashboard, so without it the only way back to the leads you were
       looking at would be the browser button. It marks itself current from
       the URL, so nothing here has to say which tab this is. -->
  <app-leads-view-switcher class="rb-switcher"></app-leads-view-switcher>

  <div class="fp-filters nq-filters">
    <mat-form-field appearance="outline" class="nq-search" subscriptSizing="dynamic">
      <mat-label>Search</mat-label>
      <input matInput [ngModel]="query.search"
             (ngModelChange)="setSearch($event)" placeholder="Name" />
    </mat-form-field>
    <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm nq-chip" *ngIf="canFilterMine"
            [class.nq-chip-on]="mineOnly" (click)="toggleMine()">Owner: me</button>
    <button type="button" class="br-btn br-btn--ghost br-icon-btn"
            (click)="reload()" matTooltip="Refresh" aria-label="Refresh">
      <br-icon>refresh</br-icon>
    </button>
  </div>

  <mat-progress-bar mode="indeterminate" *ngIf="loading"></mat-progress-bar>
  <div class="br-alert br-alert--danger nq-error" *ngIf="loadError">
    <br-icon>error_outline</br-icon> {{ loadError }}
  </div>

  <ng-container *ngIf="queue && !loadError">

    <!-- The rail. The axis is when it wakes, not who owns it or what stage it
         was in: everything here is stopped in the same place. -->
    <div class="nq-rail">
      <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm nq-chip nq-chip-all"
              [class.nq-chip-on]="query.bucket === null"
              (click)="selectBucket(null)">
        <br-icon>snooze</br-icon> Nurture <b>{{ totalAsleep }}</b>
      </button>
      <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm nq-chip"
              *ngFor="let bucket of bucketOrder"
              [class.nq-chip-on]="query.bucket === bucket"
              [class.nq-chip-late]="bucket === 'overdue'"
              (click)="selectBucket(bucket)">
        {{ bucketLabel(bucket) }} <b>{{ bucketCount(bucket) }}</b>
      </button>
    </div>

    <div class="br-alert br-alert--danger nq-error" *ngIf="actionError">
      <br-icon>block</br-icon> {{ actionError }}
    </div>

    <div class="nq-body">
      <div class="nq-list">
        <section class="nq-section" *ngFor="let section of sections">
          <header class="nq-section-head">
            <h3>{{ section.label }}</h3>
            <span class="nq-section-count">{{ section.cards.length }}</span>
          </header>

          <article class="br-card nq-card" *ngFor="let card of section.cards"
                   [class.nq-late]="section.key === 'overdue'"
                   [class.nq-wait]="section.key === 'awaiting-contract-end'">
            <div class="nq-card-top">
              <span class="nq-title">{{ card.title || 'Lead #' + card.leadId }}</span>
              <span class="nq-when">{{ wakeLine(card) }}</span>
            </div>

            <div class="nq-facts">
              <span class="nq-money" *ngIf="card.annualValue != null">
                {{ card.annualValue | currency:'USD':'symbol':'1.0-0' }}/yr</span>
              <span class="br-badge br-badge--neutral nq-chipx">
                <br-icon>snooze</br-icon> asleep {{ asleepDays(card) }}d
              </span>
              <span class="br-badge br-badge--neutral nq-chipx" *ngIf="card.sleepReasonNote">
                Reason: {{ card.sleepReasonNote }}</span>
              <span class="br-badge br-badge--neutral nq-chipx" *ngIf="!card.sleepReasonNote">
                {{ reasonLabel(card.sleepReasonCode) }}</span>
              <span class="br-badge nq-chipx nq-fit" *ngIf="card.routeFitStatus">
                fit {{ card.routeFitStatus }}</span>
              <span class="br-badge nq-chipx nq-contract" *ngIf="card.contractEnd">
                <br-icon>event</br-icon>
                {{ card.incumbentProvider || 'Incumbent' }} contract ends {{ formatDate(card.contractEnd) }}</span>
            </div>

            <!-- Why wake THIS one now. Not decoration: without it the page is a
                 list of things you failed to do. -->
            <p class="nq-reason" *ngIf="card.wakeReason">
              <span class="nq-who">{{ card.wakeReasonSource === 'Scout' ? 'SCOUT' : 'NOTE' }}</span>
              {{ card.wakeReason }}
            </p>
            <p class="nq-reason nq-reason-empty" *ngIf="!card.wakeReason">
              No wake reason recorded — nobody has said why this one is worth coming back to.
            </p>

            <div class="nq-actions">
              <button type="button" class="br-btn br-btn--primary br-btn--sm"
                      [disabled]="busyLeadId === card.leadId"
                      (click)="followUp(card)">Follow up now</button>
              <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                      [disabled]="busyLeadId === card.leadId"
                      (click)="snooze(card)">Snooze 30 days</button>
            </div>
          </article>
        </section>

        <div class="nq-empty" *ngIf="!loading && queue.items.length === 0">
          Nothing asleep here.
        </div>

        <div class="nq-pager" *ngIf="pageCount > 1">
          <button type="button" class="br-btn br-btn--ghost br-icon-btn"
                  [disabled]="queue.page <= 1" aria-label="Previous page"
                  (click)="goToPage(queue.page - 1)"><br-icon>chevron_left</br-icon></button>
          <span>{{ queue.page }} / {{ pageCount }} · {{ queue.total }} asleep</span>
          <button type="button" class="br-btn br-btn--ghost br-icon-btn"
                  [disabled]="queue.page >= pageCount" aria-label="Next page"
                  (click)="goToPage(queue.page + 1)"><br-icon>chevron_right</br-icon></button>
        </div>
      </div>

      <aside class="nq-side" *ngIf="insights">
        <section class="br-card nq-side-card">
          <h4>Why they're asleep</h4>
          <div class="nq-tally" *ngFor="let tally of insights.reasons">
            <span class="nq-tally-label">{{ reasonLabel(tally.code) }}</span>
            <span class="nq-tally-bar"><span class="nq-tally-fill"
                  [style.width.%]="reasonShare(tally)"></span></span>
            <span class="nq-tally-count">{{ tally.count }}</span>
          </div>
          <!-- The data illness the design asks the screen to point at: route fit
               that poor is Disqualified, not asleep. -->
          <p class="nq-nudge" *ngIf="misfiledRouteFit > 0">
            These {{ misfiledRouteFit }} "route fit too poor" don't belong here —
            they're Disqualified, not Nurture.
          </p>
        </section>

        <section class="br-card nq-side-card">
          <h4>Is nurture worth it</h4>
          <div class="nq-stat" *ngIf="insights.winRateAfterWaking != null">
            <b>{{ insights.winRateAfterWaking * 100 | number:'1.0-0' }}%</b>
            <span>win rate after waking</span>
          </div>
          <div class="nq-stat" *ngIf="insights.averageSleepDays != null">
            <b>{{ insights.averageSleepDays }}d</b>
            <span>average sleep</span>
          </div>
          <p class="nq-side-note" *ngIf="insights.wokenCount === 0">
            Nothing has woken yet, so there is no rate to report.
          </p>
          <p class="nq-side-note" *ngIf="insights.wokenCount > 0">
            Over {{ insights.wokenCount }} woken in the last year.
          </p>
        </section>
      </aside>
    </div>

  </ng-container>

  </div>
</div>
